import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/misc';
import { clientsSearchQuery } from './tickets-queries';
import { useDebouncedValue } from './use-debounced-value';

export interface ClientRef {
  id: number;
  name: string;
}

/**
 * Searchable client selection (GET /clients?search=) shared by dialogs.
 *
 * `contain: inline-size`: the width comes from whoever holds the picker, never
 * from its rows. A row is `truncate` (nowrap), and "name · razão social" is
 * wide; inside a Dialog (a CSS grid whose items default to `min-width: auto`)
 * that min-content widened the column past the dialog, and the footer went out
 * with it (spec 024, the "desligar cliente" dialog).
 */
export function ClientPicker({
  selected,
  onSelect,
}: {
  selected: ClientRef | null;
  onSelect: (client: ClientRef | null) => void;
}) {
  const [searchText, setSearchText] = useState('');
  const debounced = useDebouncedValue(searchText, 300);
  const clientsQ = useQuery(clientsSearchQuery(debounced));

  if (selected) {
    return (
      <div className="flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm contain-[inline-size]">
        <span className="min-w-0 truncate font-medium">{selected.name}</span>
        <Button type="button" variant="ghost" size="sm" onClick={() => onSelect(null)}>
          Alterar
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-2 contain-[inline-size]">
      <Input
        placeholder="Buscar cliente…"
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
      />
      <div className="max-h-44 overflow-y-auto rounded-md border">
        {clientsQ.isPending ? (
          <div className="flex justify-center p-3">
            <Spinner />
          </div>
        ) : clientsQ.isError ? (
          <p className="p-3 text-sm text-muted-foreground">Erro ao buscar clientes</p>
        ) : clientsQ.data.length === 0 ? (
          <p className="p-3 text-sm text-muted-foreground">Nenhum cliente encontrado</p>
        ) : (
          clientsQ.data.map((c) => (
            <button
              key={c.id}
              type="button"
              className="block w-full cursor-pointer truncate border-b px-3 py-2 text-left text-sm last:border-0 hover:bg-muted"
              onClick={() => onSelect({ id: c.id, name: c.name })}
            >
              {c.name}
              {c.social && c.social !== c.name ? (
                <span className="text-muted-foreground"> · {c.social}</span>
              ) : null}
            </button>
          ))
        )}
      </div>
    </div>
  );
}
